import React, { useState, useEffect } from "react";
import axios from "axios";
import ServiceChargeModal from './components/ServiceChargeModal';
import ProcessingModal from './components/ProcessingModal';
import APIRetryModal from './components/APIRetryModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import PaymentValidationModal from './components/PaymentValidationModal';
import ReadAloudButton from './components/ReadAloudButton';
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MMGPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check where user came from
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  const returnLabel = fromLocation === 'activity' ? 'Return to Dashboard' : 'Return to Home';
  
  // View mode state
  const [viewMode, setViewMode] = useState(false);
  const [viewingResult, setViewingResult] = useState(null);
  
  // Load user's MMG reports
  const loadUserReports = async () => {
    try {
      const response = await axios.get(`${API}/mmg/results`, { withCredentials: true });
      setReports(response.data.results || []);
    } catch (error) {
      console.error("Load reports error:", error);
    }
  };

  // Check URL for result viewing and load reports
  // Support both ?result_id={id} (legacy) and ?report={id} (new from dashboard)
  useEffect(() => {
    loadUserReports();
    
    const resultId = searchParams.get('report') || searchParams.get('result_id');
    
    if (resultId) {
      const fetchResult = async () => {
        try {
          const response = await axios.get(`${API}/mmg/result/${resultId}`, { withCredentials: true });
          
          // Parse structured sections from metadata_report if they don't exist (for old reports)
          const sections = parseMMGContent(response.data.metadata_report);
          
          // Set viewing result with structured fields
          setViewingResult({
            ...response.data,
            // Use existing structured fields if present, otherwise use parsed ones
            categories: response.data.categories || sections?.categories || '',
            seo_keywords: response.data.seo_keywords || sections?.seoKeywords || '',
            descriptions: response.data.descriptions || sections?.descriptions || '',
            hashtags: response.data.hashtags || sections?.hashtags || '',
            market_insights: response.data.market_insights || sections?.marketInsights || '',
            distribution_strategy: response.data.distribution_strategy || sections?.distributionStrategy || '',
            social_media_roi: response.data.social_media_roi || sections?.socialMediaROI || ''
          });
          
          setResultKey(prev => prev + 1); // Force refresh with latest data
          setViewMode(true);
        } catch (error) {
          console.error('Error fetching MMG result:', error);
          toast.error('Failed to load MMG result');
          navigate('/user_dashboard');
        }
      };
      fetchResult();
    }
  }, [searchParams, navigate]);

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    author_name: "",
    publisher: "Self-publish"
  });

  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [resultKey, setResultKey] = useState(0); // Force re-render key
  const [reports, setReports] = useState([]);
  
  // Payment flow states
  const [showServiceCharge, setShowServiceCharge] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing manuscript', completed: false, current: false },
    { text: 'Generating metadata', completed: false, current: false },
    { text: 'Formatting results', completed: false, current: false }
  ]);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [showPaymentValidation, setShowPaymentValidation] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);
  
  // Service info
  const [serviceInfo, setServiceInfo] = useState({
    name: "Manuscript Metadata Generator (MMG)",
    description: "Generate comprehensive SEO-optimized metadata for your manuscript",
    amount: 4.00,
    subscriberPrice: 0.00,
    isPremium: false
  });

  // Form validation
  const isFormValid = () => {
    return formData.title && formData.author_name && file;
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const validTypes = ['.pdf', '.docx'];
    const fileExt = uploadedFile.name.substring(uploadedFile.name.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(fileExt)) {
      toast.error("Please upload a PDF or DOCX file");
      return;
    }

    setFile(uploadedFile);
    toast.success("File uploaded successfully");
  };

  // Start generation
  const handleGenerate = async () => {
    
    if (!isFormValid()) {
      toast.error("Please fill in all required fields and upload a file");
      return;
    }

    try {
      // ALWAYS fetch fresh user profile before making subscription decision
      const userResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const freshUser = userResponse.data;
      
      
      // Check subscription status and role (like BOCP does)
      const isExempt = freshUser?.role === "admin" || freshUser?.role === "beta_tester";
      const isSubscriber = freshUser?.role === "subscriber" || 
                           freshUser?.subscription_status === "active" ||  // <-- THE KEY FIX!
                           freshUser?.subscription_status === "monthly" || 
                           freshUser?.subscription_status === "annual" ||
                           freshUser?.subscription_status === "Monthly" ||
                           freshUser?.subscription_status === "Annual";
      const hasPaymentMethod = freshUser?.payment_method || freshUser?.has_payment_method;
      
      // Update serviceInfo with correct pricing
      const finalPrice = (isExempt || isSubscriber) ? 0.00 : 4.00;
      setServiceInfo(prev => ({
        ...prev,
        amount: finalPrice
      }));
      
      
      if (isExempt || isSubscriber) {
        await processGeneration();
      } else if (!hasPaymentMethod) {
        toast.info("Please add a payment method to use MMG service");
        setShowPaymentValidation(true);
      } else {
        setShowServiceCharge(true);
      }
    } catch (error) {
      console.error("❌ Error in handleGenerate:", error);
      console.error("  Error response:", error.response?.data);
      console.error("  Error status:", error.response?.status);
      
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        window.location.href = '/';
      } else {
        const errorMsg = error.response?.data?.detail || error.message || "Unknown error";
        console.error("  📋 Detailed error:", errorMsg);
        
        // Show helpful message with hard refresh instruction
        toast.error(
          "Failed to verify subscription status. If you just subscribed or changed your profile, please do a hard refresh: " +
          (navigator.platform.includes('Mac') ? "Cmd+Shift+R" : "Ctrl+Shift+R"),
          { duration: 8000 }
        );
      }
    }
  };

  // Handle service charge confirmation
  const handleServiceChargeConfirm = async () => {
    setShowServiceCharge(false);
    // Just proceed to generation - backend will charge AFTER successful generation
    await processGeneration();
  };

  // Process generation
  const processGeneration = async () => {
    try {
      setShowProcessingModal(true);
      
      // Step 1: Upload and analyze manuscript
      setCurrentStep('Uploading and analyzing manuscript...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: false, current: true },
        { text: 'Generating metadata', completed: false, current: false },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      setProcessingStatus('Uploading manuscript...');
      
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('subtitle', formData.subtitle || '');
      formDataToSend.append('author_name', formData.author_name);
      formDataToSend.append('publisher', formData.publisher);
      formDataToSend.append('file', file);
      
      // Simulate progress during upload
      setProcessingProgress(25);
      
      // Step 2: Generate metadata
      setCurrentStep('Generating SEO-optimized metadata and categories...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating metadata', completed: false, current: true },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      setProcessingStatus('Analyzing content and generating metadata...');
      
      const response = await axios.post(
        `${API}/mmg/generate`,
        formDataToSend,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000
        }
      );

      // Step 3: Formatting results
      setCurrentStep('Finalizing your comprehensive metadata report...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating metadata', completed: true, current: false },
        { text: 'Formatting results', completed: false, current: true }
      ]);
      setProcessingProgress(80);
      setProcessingStatus('Metadata generated successfully!');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating metadata', completed: true, current: false },
        { text: 'Formatting results', completed: true, current: false }
      ]);
      setProcessingProgress(95);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Log the response to debug
      
      // DEBUG: Check structured data fields
      
      // OPTION A: Force Full Data Reload from Database (for consistency)
      // Instead of using response.data directly, fetch complete report from database
      const reportId = response.data.id;
      
      // Store charge info before reload
      const wasCharged = response.data.charged;
      const transactionId = response.data.transaction_id;
      const chargeAmount = response.data.charge_amount;
      
      // Reload complete report from database (guarantees all 7 sections)
      await loadAndDisplayReport(reportId);
      setResultKey(prev => prev + 1); // Force re-render with new data
      
      if (wasCharged) {
        setLastTransactionId(transactionId);
        setLastCharge({
          serviceName: "MMG",
          amount: chargeAmount,
          bookTitle: formData.title
        });
      }
      
      // Complete progress
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setShowProcessingModal(false);
      
      localStorage.setItem('serviceCompleted', 'mmg');
      
      // ALWAYS show result first, THEN show email modal on top if charged
      setShowResult(true);
      
      // If charged, show email receipt modal AFTER result is set
      if (wasCharged) {
        setTimeout(() => {
          setShowEmailReceiptModal(true);
        }, 500);
      } else {
        // Not charged (subscriber), results already showing
        toast.success("Metadata generated successfully!");
      }
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
      
    } catch (error) {
      console.error("Generation error:", error);
      setShowProcessingModal(false);
      if (error.code === 'ECONNABORTED') {
        toast.error("Generation timed out. Please try again.");
      } else {
        // Handle object or string error detail
        const errorDetail = error.response?.data?.detail;
        const errorMessage = typeof errorDetail === 'string' 
          ? errorDetail 
          : errorDetail?.message || "Failed to generate metadata";
        toast.error(errorMessage);
      }
    }
  };

  // Handle send receipt
  const handleSendReceipt = async () => {
    if (!lastTransactionId) {
      console.error('❌ No transaction_id available');
      toast.error('No transaction ID found. Cannot send receipt.');
      setShowEmailReceiptModal(false);
      setShowResult(true);
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/send-receipt-email`,
        {
          transaction_id: lastTransactionId,
          user_email: user.email
        },
        { withCredentials: true }
      );
      toast.success("Receipt email sent successfully!");
      setShowEmailReceiptModal(false);
      // Show results after sending receipt
      setShowResult(true);
    } catch (error) {
      console.error('❌ Receipt email error:', error.response?.data || error.message);
      toast.error(`Receipt email not sent: ${error.response?.data?.detail || error.message}`);
      setShowEmailReceiptModal(false);
      // Show results even if email fails
      setShowResult(true);
    }
  };

  const handleCloseEmailReceiptModal = () => {
    setShowEmailReceiptModal(false);
    toast.success("Metadata generated successfully!");
    // Show results when user clicks "No Thanks"
    setShowResult(true);
  };

  // Format report text - convert markdown-style headers to HTML (Times New Roman font)
  // Handle new report - reset form
  const handleNewReport = () => {
    setFormData({
      title: '',
      subtitle: '',
      author_name: '',
      publisher: ''
    });
    setFile(null);
    setResult(null);
    setShowResult(false);
    
    // Clear file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    toast.success("Form cleared for new metadata generation!");
  };

  // Load and display a specific report
  const loadAndDisplayReport = async (reportId) => {
    try {
      setResult(null);
      setShowResult(false);
      
      const response = await axios.get(`${API}/mmg/result/${reportId}`, { withCredentials: true });
      
      // Parse structured sections from metadata_report if they exist
      const sections = parseMMGContent(response.data.metadata_report);
      
      setResult({
        title: response.data.title,
        subtitle: response.data.subtitle,
        author_name: response.data.author_name,
        publisher: response.data.publisher,
        metadata_report: response.data.metadata_report,
        created_at: response.data.created_at,
        // Add structured fields from parsing or from response if already structured
        categories: response.data.categories || sections?.categories,
        seo_keywords: response.data.seo_keywords || sections?.seoKeywords,
        descriptions: response.data.descriptions || sections?.descriptions,
        hashtags: response.data.hashtags || sections?.hashtags,
        market_insights: response.data.market_insights || sections?.marketInsights,
        distribution_strategy: response.data.distribution_strategy || sections?.distributionStrategy,
        social_media_roi: response.data.social_media_roi || sections?.socialMediaROI
      });
      
      // Update form data to match the loaded report
      setFormData({
        title: response.data.title || '',
        subtitle: response.data.subtitle || '',
        author_name: response.data.author_name || '',
        publisher: response.data.publisher || ''
      });
      
      setShowResult(true);
      toast.success("Report loaded successfully!");
      
      // Scroll to results
      setTimeout(() => {
        const resultsElement = document.getElementById('mmg-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      toast.error("Failed to load report");
      console.error("Load report error:", error);
    }
  };

  const formatReportText = (text) => {
    if (!text) return '';
    
    // Split into lines
    const lines = text.split('\n');
    let formattedHtml = '';
    let inList = false;
    
    lines.forEach((line, index) => {
      // Remove ### and format as headers
      if (line.trim().startsWith('###')) {
        if (inList) {
          formattedHtml += '</div>';
          inList = false;
        }
        const headerText = line.replace(/^###\s*/, '').trim();
        formattedHtml += `<h3 style="color: #1e40af; font-size: 18px; font-weight: 700; margin-top: 20px; margin-bottom: 10px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; font-family: 'Times New Roman', Times, serif;">${headerText}</h3>`;
      }
      // Format section dividers (===)
      else if (line.trim().match(/^={3,}$/)) {
        if (inList) {
          formattedHtml += '</div>';
          inList = false;
        }
        formattedHtml += '<hr style="border: none; border-top: 2px solid #e5e7eb; margin: 16px 0;" />';
      }
      // Format bullet points
      else if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('#')) {
        if (!inList) {
          formattedHtml += '<div style="margin-bottom: 8px;">';
          inList = true;
        }
        const bulletText = line.replace(/^[•\-#]\s*/, '').trim();
        formattedHtml += `<div style="margin-left: 20px; margin-bottom: 4px; font-family: 'Times New Roman', Times, serif; line-height: 1.5;"><span style="color: #f97316; font-weight: 600;">• </span>${bulletText}</div>`;
      }
      // Bold text (look for **text**)
      else if (line.includes('**')) {
        if (inList) {
          formattedHtml += '</div>';
          inList = false;
        }
        let formatted = line.replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #1e293b; font-family: \'Times New Roman\', Times, serif;">$1</strong>');
        formattedHtml += `<p style="margin-bottom: 8px; line-height: 1.6; font-family: 'Times New Roman', Times, serif;">${formatted}</p>`;
      }
      // Regular paragraphs
      else if (line.trim().length > 0) {
        if (inList) {
          formattedHtml += '</div>';
          inList = false;
        }
        formattedHtml += `<p style="margin-bottom: 8px; line-height: 1.6; font-family: 'Times New Roman', Times, serif;">${line}</p>`;
      }
      // Reduce empty line spacing - only add minimal spacing
      else if (line.trim().length === 0 && index > 0 && lines[index - 1]?.trim().length > 0) {
        // Only add a small spacer between sections, not <br />
        formattedHtml += '<div style="height: 4px;"></div>';
      }
    });
    
    if (inList) {
      formattedHtml += '</div>';
    }
    
    return formattedHtml;
  };

  // Print results with Times New Roman font and larger logo
  const printResults = () => {
    if (!result && !viewingResult) {
      toast.error("No report to print");
      return;
    }
    
    const displayResult = viewMode ? viewingResult : result;
    
    // Build structured HTML for all 7 sections
    const buildStructuredHTML = () => {
      let html = '';
      
      if (displayResult.categories) {
        html += `
          <div class="section" style="background: #dbeafe; border-left-color: #3b82f6;">
            <h4 style="color: #1e3a8a;">📂 Primary & Secondary Categories</h4>
            <p style="white-space: pre-wrap; color: #1e40af;">${displayResult.categories}</p>
          </div>
        `;
      }
      
      if (displayResult.seo_keywords) {
        html += `
          <div class="section" style="background: #dcfce7; border-left-color: #22c55e;">
            <h4 style="color: #14532d;">🔍 SEO Keywords & Search Optimization</h4>
            <p style="white-space: pre-wrap; color: #15803d;">${displayResult.seo_keywords}</p>
          </div>
        `;
      }
      
      if (displayResult.descriptions) {
        html += `
          <div class="section" style="background: #f3e8ff; border-left-color: #a855f7;">
            <h4 style="color: #581c87;">📖 Book Descriptions</h4>
            <p style="white-space: pre-wrap; color: #6b21a8;">${displayResult.descriptions}</p>
          </div>
        `;
      }
      
      if (displayResult.hashtags) {
        html += `
          <div class="section" style="background: #fce7f3; border-left-color: #ec4899;">
            <h4 style="color: #831843;">#️⃣ Social Media Hashtags</h4>
            <p style="white-space: pre-wrap; color: #9f1239;">${displayResult.hashtags}</p>
          </div>
        `;
      }
      
      if (displayResult.market_insights) {
        html += `
          <div class="section" style="background: #fef3c7; border-left-color: #eab308;">
            <h4 style="color: #92400e;">🎯 Market Insights & Competitive Analysis</h4>
            <p style="white-space: pre-wrap; color: #78350f;">${displayResult.market_insights}</p>
          </div>
        `;
      }
      
      if (displayResult.distribution_strategy) {
        html += `
          <div class="section" style="background: #fed7aa; border-left-color: #f97316;">
            <h4 style="color: #7c2d12;">🚀 Distribution Strategy</h4>
            <p style="white-space: pre-wrap; color: #9a3412;">${displayResult.distribution_strategy}</p>
          </div>
        `;
      }
      
      if (displayResult.social_media_roi) {
        html += `
          <div class="section" style="background: #e0e7ff; border-left-color: #6366f1;">
            <h4 style="color: #312e81;">📱 Social Media ROI Recommendations</h4>
            <p style="white-space: pre-wrap; color: #3730a3;">${displayResult.social_media_roi}</p>
          </div>
        `;
      }
      
      return html || `<p style="white-space: pre-wrap;">${displayResult.metadata_report}</p>`;
    };
    
    setTimeout(() => {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Digital Ink AI - MMG Report</title>
              <style>
                body { 
                  font-family: 'Times New Roman', Times, serif;
                  font-size: 12pt;
                  margin: 15px;
                  line-height: 1.5;
                  color: #333;
                }
                .header { 
                  text-align: center; 
                  margin-bottom: 20px;
                  border-bottom: 1px solid #ddd;
                  padding-bottom: 15px;
                }
                .header img { 
                  height: 50px;
                  margin-bottom: 10px;
                }
                .header h1 {
                  font-size: 20pt;
                  margin: 8px 0;
                  font-family: 'Times New Roman', Times, serif;
                }
                .header p {
                  font-size: 11pt;
                  margin: 3px 0;
                }
                .report-content { 
                  max-width: 800px;
                  margin: 0 auto;
                }
                .section {
                  padding: 10px;
                  margin-bottom: 10px;
                  border-left: 4px solid;
                  border-radius: 6px;
                  page-break-inside: avoid;
                }
                h4 { 
                  margin: 0 0 6px 0;
                  font-size: 14pt;
                  font-weight: bold;
                  font-family: 'Times New Roman', Times, serif;
                }
                p {
                  margin: 0;
                  font-size: 12pt;
                  line-height: 1.5;
                }
                p {
                  margin: 0;
                  color: #475569;
                  font-size: 14px;
                }
                .footer { 
                  margin-top: 40px; 
                  text-align: center; 
                  font-size: 10pt; 
                  color: #666; 
                  border-top: 1px solid #ddd; 
                  padding-top: 20px; 
                }
                @media print { 
                  body { margin: 0; padding: 20px; } 
                  .no-print { display: none; } 
                }
              </style>
            </head>
            <body>
              <div class="header">
                <img src="/Digital_Ink_AI_Logo_Gold.png" alt="Digital Ink AI" crossOrigin="anonymous" />
                <h1>Manuscript Metadata Generator</h1>
                <div style="margin-top: 20px;">
                  <div style="font-size: 20px; font-weight: 700; color: #0f172a;">${formData.title || displayResult.title || 'Untitled'}</div>
                  ${formData.subtitle ? `<div style="font-size: 16px; color: #64748b; font-style: italic;">${formData.subtitle}</div>` : ''}
                  <div style="font-size: 16px; color: #475569; margin-top: 8px;">by ${formData.author_name || displayResult.author_name || 'Unknown Author'}</div>
                  <div style="font-size: 12px; color: #94a3b8; margin-top: 8px;">Generated ${new Date().toLocaleDateString('en-US', { timeZone: 'America/Denver' })}</div>
                </div>
              </div>
              
              <div class="report-content">
                ${buildStructuredHTML()}
              </div>
              
              <div class="footer">
                <p>Generated with Digital Ink AI - www.DigitalInkAI.com</p>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      } else {
        toast.error("Could not open print window. Please check your popup blocker.");
      }
    }, 100);
  };

  // Export to PDF (simplified approach matching BVADM exactly)
  // Helper function to render structured JSON data
  const renderStructuredData = (data) => {
    if (!data) return null;
    
    // Try to parse as JSON
    try {
      const parsed = JSON.parse(data);
      return (
        <div className="space-y-3">
          {Object.entries(parsed).map(([key, value]) => (
            <div key={key}>
              <h5 className="font-semibold text-slate-900 capitalize mb-1">
                {key.replace(/_/g, ' ')}:
              </h5>
              {Array.isArray(value) ? (
                <ul className="list-disc list-inside ml-4 space-y-1">
                  {value.map((item, idx) => (
                    <li key={idx} className="text-slate-700">
                      {typeof item === 'object' ? JSON.stringify(item, null, 2) : item}
                    </li>
                  ))}
                </ul>
              ) : typeof value === 'object' ? (
                <pre className="bg-slate-100 p-2 rounded text-sm whitespace-pre-wrap">
                  {JSON.stringify(value, null, 2)}
                </pre>
              ) : (
                <p className="text-slate-700">{value}</p>
              )}
            </div>
          ))}
        </div>
      );
    } catch (e) {
      // Not JSON, render as text
      return <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{data}</p>;
    }
  };

  // Helper function to parse MMG content into sections (Skip Section 1 - Title Suggestions)
  const parseMMGContent = (content) => {
    if (!content) return null;
    
    // Try to extract sections using regex - more flexible patterns
    // NOTE: We skip Section 1 (Title Suggestions) per user request
    const sections = {};
    
    // Section 2: Categories - look for content between section 2 and 3
    const catMatch = content.match(/(?:^|\n)[\s#]*\*{0,2}(?:2|1)\.?\s*\*{0,2}[^\n]*?(?:CATEGORIES|categories|PRIMARY|primary)[^\n]*?\*{0,2}\s*\n([\s\S]*?)(?=(?:\n[\s#]*\*{0,2}(?:3|2)\.|\n[\s#]*\#{2,3}\s*(?:SEO|seo)))/i);
    if (catMatch) sections.categories = catMatch[1].trim();
    
    // Section 3: SEO Keywords - look for content between section 3 and 4
    const seoMatch = content.match(/(?:^|\n)[\s#]*\*{0,2}(?:3|2)\.?\s*\*{0,2}[^\n]*?(?:SEO|KEYWORDS|seo|keywords)[^\n]*?\*{0,2}\s*\n([\s\S]*?)(?=(?:\n[\s#]*\*{0,2}(?:4|3)\.|\n[\s#]*\#{2,3}\s*(?:BOOK|book|DESCRIPTIONS|descriptions)))/i);
    if (seoMatch) sections.seoKeywords = seoMatch[1].trim();
    
    // Section 4: Book Descriptions - look for content between section 4 and 5
    const descMatch = content.match(/(?:^|\n)[\s#]*\*{0,2}(?:4|3)\.?\s*\*{0,2}[^\n]*?(?:BOOK|book|DESCRIPTIONS|descriptions)[^\n]*?\*{0,2}\s*\n([\s\S]*?)(?=(?:\n[\s#]*\*{0,2}(?:5|4)\.|\n[\s#]*\#{2,3}\s*(?:SOCIAL|social|HASHTAGS|hashtags)))/i);
    if (descMatch) sections.descriptions = descMatch[1].trim();
    
    // Section 5: Hashtags - look for content between section 5 and 6
    const hashMatch = content.match(/(?:^|\n)[\s#]*\*{0,2}(?:5|4)\.?\s*\*{0,2}[^\n]*?(?:SOCIAL|social|HASHTAGS|hashtags)[^\n]*?\*{0,2}\s*\n([\s\S]*?)(?=(?:\n[\s#]*\*{0,2}(?:6|5)\.|\n[\s#]*\#{2,3}\s*(?:MARKET|market)))/i);
    if (hashMatch) sections.hashtags = hashMatch[1].trim();
    
    // Section 6: Market Insights - look for content between section 6 and 7
    const marketMatch = content.match(/(?:^|\n)[\s#]*\*{0,2}(?:6|5)\.?\s*\*{0,2}[^\n]*?(?:MARKET|market|INSIGHTS|insights)[^\n]*?\*{0,2}\s*\n([\s\S]*?)(?=(?:\n[\s#]*\*{0,2}(?:7|6)\.|\n[\s#]*\#{2,3}\s*(?:DISTRIBUTION|distribution)))/i);
    if (marketMatch) sections.marketInsights = marketMatch[1].trim();
    
    // Section 7: Distribution - look for content between section 7 and 8
    const distMatch = content.match(/(?:^|\n)[\s#]*\*{0,2}(?:7|6)\.?\s*\*{0,2}[^\n]*?(?:DISTRIBUTION|distribution)[^\n]*?\*{0,2}\s*\n([\s\S]*?)(?=(?:\n[\s#]*\*{0,2}(?:8|7)\.|\n[\s#]*\#{2,3}\s*(?:SOCIAL|social|ROI|roi)))/i);
    if (distMatch) sections.distributionStrategy = distMatch[1].trim();
    
    // Section 8: Social Media ROI - look for content after section 8 to end or next major header
    const roiMatch = content.match(/(?:^|\n)[\s#]*\*{0,2}(?:8|7)\.?\s*\*{0,2}[^\n]*?(?:SOCIAL|social|ROI|roi)[^\n]*?\*{0,2}\s*\n([\s\S]*?)(?=(?:\n[\s#]*\#{2,3}\s*(?:IMPLEMENTATION|implementation|NEXT|next))|$)/i);
    if (roiMatch) sections.socialMediaROI = roiMatch[1].trim();
    
    return sections;
  };

  const exportToPDF = () => {
    const data = viewMode ? viewingResult : result;
    if (!data) {
      toast.error("No report to export");
      return;
    }
    
    try {
      toast.info("Generating PDF... Please wait");
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 170;
      const leftMargin = 20;
      let yPosition = 20;
      
      // Helper function for page breaks
      const checkNewPage = (spaceNeeded) => {
        if (yPosition + spaceNeeded > 280) {
          pdf.addPage();
          yPosition = 20;
        }
      };
      
      // Helper function to add multi-line text
      const addMultiLineText = (text, fontSize = 10, maxWidth = pageWidth) => {
        if (!text) return;
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          checkNewPage(7);
          pdf.text(line, leftMargin, yPosition);
          yPosition += 6;
        });
      };
      
      // HEADER
      const now = new Date();
      const timestamp = now.toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true
      });
      const generatedDate = now.toLocaleDateString('en-US', {
        month: '2-digit', day: '2-digit', year: 'numeric'
      });
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(timestamp, leftMargin, yPosition);
      pdf.text('Digital Ink AI - MMG Report', 190, yPosition, { align: 'right' });
      yPosition += 10;
      
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('DIGITAL INK AI', 105, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Manuscript Metadata Generator', 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // BOOK INFO
      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(0, 0, 0);
      const title = data.title || formData.title || 'Untitled';
      const titleLines = pdf.splitTextToSize(title, pageWidth);
      titleLines.forEach(line => {
        pdf.text(line, 105, yPosition, { align: 'center' });
        yPosition += 7;
      });
      yPosition += 5;
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      const author = data.author_name || formData.author_name || 'Unknown Author';
      pdf.text(`by ${author}`, 105, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated ${generatedDate}`, 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      pdf.setTextColor(0, 0, 0);
      
      // CATEGORIES
      if (data.categories) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Categories & Keywords', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(data.categories);
        yPosition += 5;
      }
      
      // SEO KEYWORDS
      if (data.seo_keywords) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('SEO Keywords', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(data.seo_keywords);
        yPosition += 5;
      }
      
      // DESCRIPTIONS
      if (data.descriptions) {
        checkNewPage(30);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Book Descriptions', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(data.descriptions);
        yPosition += 5;
      }
      
      // HASHTAGS
      if (data.hashtags) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Social Media Hashtags', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(data.hashtags);
        yPosition += 5;
      }
      
      // MARKET INSIGHTS
      if (data.market_insights) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Market Insights', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(data.market_insights);
        yPosition += 5;
      }
      
      // DISTRIBUTION STRATEGY
      if (data.distribution_strategy) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Distribution Strategy', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(data.distribution_strategy);
        yPosition += 5;
      }
      
      // SOCIAL MEDIA ROI
      if (data.social_media_roi) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Social Media ROI Strategy', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(data.social_media_roi);
        yPosition += 5;
      }
      
      // Footer on all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${totalPages}`, 105, 287, { align: 'center' });
        pdf.text('www.DigitalInkAI.com', 105, 292, { align: 'center' });
      }
      
      const safeTitle = (data.title || formData.title || 'Report').replace(/[^a-z0-9]/gi, '_');
      const filename = `MMG_${safeTitle}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error('PDF error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`);
    }
  };

  // Render result - PDE/BREC Template Style
  const renderResult = () => {
    if (!showResult || !result) return null;

    return (
      <div className="max-w-6xl mx-auto">
        {/* Report Header - PDE Style */}
        <div className="bg-white rounded-lg shadow-lg border border-slate-200 mb-6 p-8">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">MMG Analysis Report</h1>
              <p className="text-slate-600 text-lg">
                Metadata Generation for {formData.title}
              </p>
              {formData.author_name && (
                <p className="text-slate-600 mt-1">by {formData.author_name}</p>
              )}
            </div>
            <div className="flex gap-3 print:hidden">
              <button 
                onClick={printResults} 
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                🖨️ Print
              </button>
              <button 
                onClick={exportToPDF} 
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                📄 Export PDF
              </button>
              <button
                onClick={handleNewReport}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                ✨ New Analysis
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t">
            <div>
              <p className="text-slate-500 text-sm mb-1">Book Title:</p>
              <p className="font-bold text-slate-900">{formData.title}</p>
            </div>
            {formData.author_name && (
              <div>
                <p className="text-slate-500 text-sm mb-1">Author:</p>
                <p className="font-bold text-slate-900">{formData.author_name}</p>
              </div>
            )}
            <div>
              <p className="text-slate-500 text-sm mb-1">Service:</p>
              <p className="font-bold text-slate-900">Metadata Generator</p>
            </div>
          </div>
        </div>

        {/* Report Sections */}
        <div className="space-y-6">
          {/* Section 1: Categories */}
          {result.categories && (
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                <span className="text-3xl mr-3">📂</span>
                Primary & Secondary Categories
              </h3>
              <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                {result.categories}
              </div>
            </div>
          )}

          {/* Section 2: SEO Keywords */}
          {result.seo_keywords && (
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                <span className="text-3xl mr-3">🔍</span>
                SEO Keywords & Search Optimization
              </h3>
              <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                {result.seo_keywords}
              </div>
            </div>
          )}

          {/* Section 3: Book Descriptions */}
          {result.descriptions && (
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                <span className="text-3xl mr-3">📖</span>
                Book Descriptions
              </h3>
              <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                {result.descriptions}
              </div>
            </div>
          )}

          {/* Section 4: Social Media Hashtags */}
          {result.hashtags && (
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                <span className="text-3xl mr-3">#️⃣</span>
                Social Media Hashtags
              </h3>
              <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                {result.hashtags}
              </div>
            </div>
          )}

          {/* Section 5: Market Insights */}
          {result.market_insights && (
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                <span className="text-3xl mr-3">🎯</span>
                Market Insights & Competitive Analysis
              </h3>
              <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                {result.market_insights}
              </div>
            </div>
          )}

          {/* Section 6: Distribution Strategy */}
          {result.distribution_strategy && (
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                <span className="text-3xl mr-3">🚀</span>
                Distribution Strategy
              </h3>
              <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                {result.distribution_strategy}
              </div>
            </div>
          )}

          {/* Section 7: Social Media ROI */}
          {result.social_media_roi && (
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                <span className="text-3xl mr-3">📱</span>
                Social Media ROI Recommendations
              </h3>
              <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                {result.social_media_roi}
              </div>
            </div>
          )}

          {/* Fallback for old reports */}
          {!result.categories && !result.seo_keywords && result.metadata_report && (
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-orange-700 mb-4">Metadata Report</h3>
              <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                {result.metadata_report}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // View mode for existing results from Dashboard - PDE Template Style
  if (viewMode && viewingResult) {
    return (
      <div className="min-h-screen bg-slate-50" key={resultKey}>
        {/* Top Navigation Bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <img 
              src="/Digital_Ink_AI_Logo_Gold.png" 
              alt="Digital Ink AI" 
              className="h-10"
              crossOrigin="anonymous"
            />
            <div className="flex gap-4">
              <button 
                onClick={() => navigate('/homepage')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                🏠 Home
              </button>
              <button 
                onClick={() => navigate('/user_dashboard')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Report Header Card */}
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 mb-6 p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-slate-900 mb-2">MMG Analysis Report</h1>
                <p className="text-slate-600 text-lg">
                  Metadata Generation for {viewingResult.title}
                </p>
                {viewingResult.author_name && (
                  <p className="text-slate-600 mt-1">by {viewingResult.author_name}</p>
                )}
              </div>
              <div className="flex gap-3 print:hidden">
                <button 
                  onClick={printResults} 
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  🖨️ Print
                </button>
                <button 
                  onClick={exportToPDF} 
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  📄 Export PDF
                </button>
                <button
                  onClick={() => {
                    setViewMode(false);
                    setViewingResult(null);
                    handleNewReport();
                    navigate('/mmg');
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  ✨ New Analysis
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t">
              <div>
                <p className="text-slate-500 text-sm mb-1">Book Title:</p>
                <p className="font-bold text-slate-900">{viewingResult.title}</p>
              </div>
              {viewingResult.author_name && (
                <div>
                  <p className="text-slate-500 text-sm mb-1">Author:</p>
                  <p className="font-bold text-slate-900">{viewingResult.author_name}</p>
                </div>
              )}
              <div>
                <p className="text-slate-500 text-sm mb-1">Service:</p>
                <p className="font-bold text-slate-900">Metadata Generator</p>
              </div>
            </div>
          </div>

          {/* Report Sections - Individual White Cards */}
          <div id="mmg-print-content" className="space-y-6">
            {/* Report Sections - Individual White Cards */}
            
            {/* Section 1: Categories */}
            {viewingResult.categories && (
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                  <span className="text-3xl mr-3">📂</span>
                  Primary & Secondary Categories
                </h3>
                <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                  {viewingResult.categories}
                </div>
              </div>
            )}

            {/* Section 2: SEO Keywords */}
            {viewingResult.seo_keywords && (
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                  <span className="text-3xl mr-3">🔍</span>
                  SEO Keywords & Search Optimization
                </h3>
                <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                  {viewingResult.seo_keywords}
                </div>
              </div>
            )}

            {/* Section 3: Book Descriptions */}
            {viewingResult.descriptions && (
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                  <span className="text-3xl mr-3">📖</span>
                  Book Descriptions
                </h3>
                <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                  {viewingResult.descriptions}
                </div>
              </div>
            )}

            {/* Section 4: Social Media Hashtags */}
            {viewingResult.hashtags && (
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                  <span className="text-3xl mr-3">#️⃣</span>
                  Social Media Hashtags
                </h3>
                <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                  {viewingResult.hashtags}
                </div>
              </div>
            )}

            {/* Section 5: Market Insights */}
            {viewingResult.market_insights && (
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                  <span className="text-3xl mr-3">🎯</span>
                  Market Insights & Competitive Analysis
                </h3>
                <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                  {viewingResult.market_insights}
                </div>
              </div>
            )}

            {/* Section 6: Distribution Strategy */}
            {viewingResult.distribution_strategy && (
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                  <span className="text-3xl mr-3">🚀</span>
                  Distribution Strategy
                </h3>
                <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                  {viewingResult.distribution_strategy}
                </div>
              </div>
            )}

            {/* Section 7: Social Media ROI */}
            {viewingResult.social_media_roi && (
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                <h3 className="text-2xl font-bold text-orange-700 mb-4 flex items-center">
                  <span className="text-3xl mr-3">📱</span>
                  Social Media ROI Recommendations
                </h3>
                <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                  {viewingResult.social_media_roi}
                </div>
              </div>
            )}

            {/* Fallback for old reports */}
            {!viewingResult.categories && !viewingResult.seo_keywords && viewingResult.metadata_report && (
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8">
                <h3 className="text-2xl font-bold text-orange-700 mb-4">Metadata Report</h3>
                <div className="prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
                  {viewingResult.metadata_report}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center py-8 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Generated by Digital Ink AI - AI-Powered Publishing Tools
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo_Gold-rbg.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/homepage')}>
                🏠 Home
              </Button>
              <Button variant="outline" onClick={() => navigate('/user_dashboard')}>
                📊 Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title and Description */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            📊 Manuscript Metadata Generator
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Transform your manuscript into a market-ready product with AI-generated SEO metadata, targeted keywords, BISAC categories, compelling descriptions, and strategic social media hashtags. Get comprehensive, publication-ready metadata that maximizes discoverability across all digital platforms and optimizes your book's commercial potential.
          </p>
        </div>

        {/* Pricing Info */}
        {user && (
          <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
            <div className="text-center">
              {user.subscription_status !== 'None' || user.subscription_status === 'active' || user.role === 'admin' || user.role === 'beta_tester' ? (
                <div>
                  <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                  <p className="text-green-700">Included with your subscription - generate as much metadata as you need!</p>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">💎 Standard Service - $4.00</h3>
                  <p className="text-blue-700">Professional metadata generation charged to your payment method on file</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Single-Column Layout (matching PDE template) */}
        <div className="max-w-6xl mx-auto">
          {/* Main Content - Form and Results */}
          <div>
            {/* New Report Button */}
            {showResult && (
              <div className="mb-6 flex justify-start">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleNewReport}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                >
                  📝 New Metadata Report
                </Button>
              </div>
            )}
            
            {!showResult && (
              <Card>
                <CardHeader>
                  <div className="text-center mb-4">
                    <img 
                      src="/Digital_Ink_AI_Logo_Gold.png" 
                      alt="Digital Ink AI" 
                      className="h-10 mx-auto mb-3"
                      crossOrigin="anonymous"
                    />
                    <CardTitle className="text-2xl text-slate-900">📊 Manuscript Submission</CardTitle>
                    <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Book Title *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder="Enter book title"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="subtitle">Subtitle</Label>
                      <Input
                        id="subtitle"
                        value={formData.subtitle}
                        onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                        placeholder="Enter subtitle (optional)"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="author_name">Author Name *</Label>
                      <Input
                        id="author_name"
                        value={formData.author_name}
                        onChange={(e) => setFormData({...formData, author_name: e.target.value})}
                        placeholder="Enter author name"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="publisher">Publisher</Label>
                      <Input
                        id="publisher"
                        value={formData.publisher}
                        onChange={(e) => setFormData({...formData, publisher: e.target.value})}
                        placeholder="Publisher name (optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="file">Upload Manuscript (PDF or DOCX) *</Label>
                    <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        id="file"
                        accept=".pdf,.docx"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <label htmlFor="file" className="cursor-pointer">
                        <div className="text-4xl mb-4">📁</div>
                        {file ? (
                          <div>
                            <p className="text-green-600 font-medium">{file.name}</p>
                            <p className="text-sm text-slate-500">
                              {(file.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-slate-600 font-medium">Click to select file</p>
                            <p className="text-sm text-slate-500">PDF or DOCX files only (max 50MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>What You'll Receive:</strong> SEO-optimized metadata including title suggestions, 12 social media hashtags, BISAC categories, keywords, book descriptions, and market insights.
                    </p>
                  </div>

                  <Button
                    onClick={handleGenerate}
                    disabled={!isFormValid() || showProcessingModal}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transform transition hover:scale-105"
                  >
                    {showProcessingModal ? (
                      <>
                        <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                        Generating Metadata with Digital Ink AI...
                      </>
                    ) : (
                      <>
                        <BookOpen className="mr-2 h-5 w-5" />
                        🚀 Start Digital Ink AI Metadata Generation
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Results Display */}
            {renderResult()}
          </div>
        </div>

        {/* Modals */}
        <ServiceChargeModal
          isOpen={showServiceCharge}
          onClose={() => setShowServiceCharge(false)}
          onConfirm={handleServiceChargeConfirm}
          serviceInfo={serviceInfo}
        />

        <ProcessingModal
          isOpen={showProcessingModal}
          serviceName="Manuscript Metadata Generator (MMG)"
          bookTitle={formData.title}
          progress={processingProgress}
          currentStep={currentStep}
          progressSteps={progressSteps}
        />

        <EmailReceiptModal
          isOpen={showEmailReceiptModal}
          onClose={handleCloseEmailReceiptModal}
          onSendReceipt={handleSendReceipt}
          chargeInfo={lastCharge}
        />

        <PaymentValidationModal
          isOpen={showPaymentValidation}
          onClose={() => setShowPaymentValidation(false)}
          user={user}
        />
      </main>
    </div>
  );
}

export default MMGPage;
